/**
 * One device, one card — the Task 7 mockup
 * (`applications/super-admin-ui/mockups/fleet-device-card.html`), built with
 * this app's own Tailwind/lucide vocabulary instead of the mockup's raw CSS.
 *
 * Every visible string is a NAME, never an id: `deviceId`, `unitId`,
 * `tenantId` and `locationId` live only in `title` attributes, exactly as the
 * mockup's own rule states. Presentation helpers (pill styles, version
 * shortening, install-cell, the reinstall guard) come from
 * `fleetPresentation.tsx` so the card and the list view can never disagree on
 * what "on target" looks like.
 *
 * The `⋯` button opens `FleetRowMenu` (Task 9) — the SAME component the list
 * view uses, so the two views can never disagree on what the menu offers.
 * Channel flip and remove are pre-existing actions (already live on the
 * table today) and stay wired here directly, on the face of the card.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, History, LifeBuoy, RotateCcw, Trash2, Monitor } from 'lucide-react';
import ShimmerImage from '../../../components/ShimmerImage';
import { toastService } from '../../../services/toastService';
import FleetRowMenu from './FleetRowMenu';
import T3CardReadersLine from './T3CardReadersLine';
import { isSunmiT3Model } from '../../../components/pos/t3CardSetup';
import { isCardSetupModel } from '../../../components/pos/cardSetup/cardSetupModel';
import {
  classifyHardwareMatch,
  isHardwareMismatch,
  type FleetRow,
} from './fleetRows';
import type { DeviceModelRow } from '../../../services/hardwareApi';
import {
  STATUS_STYLE,
  TARGET_SOURCE_STYLE,
  RING_STYLE,
  SOURCE_STYLE,
  shortVersion,
  installedOn,
  installedAtFull,
  installCell,
  reinstallBlock,
  checkInAgo,
  STALE_SEEN_MS,
} from './fleetPresentation';

export default function FleetDeviceCard({
  row,
  model,
  tenantName,
  order,
  registerUnresolved = false,
  onSupport,
  onHistory,
  onReinstall,
  onRemove,
  onFlipChannel,
  onChanged,
  onReprovision,
}: {
  row: FleetRow;
  model?: DeviceModelRow;
  tenantName?: string;
  order?: { orderNo: string; status: string };
  /** True when THIS device's register lookup failed — "we don't know", never "not paired". */
  registerUnresolved?: boolean;
  onSupport: () => void;
  onHistory: () => void;
  onReinstall: () => void;
  onRemove: () => void;
  onFlipChannel: () => void;
  /** Re-fetch the fleet after the `⋯` menu changes something (pin/ring). */
  onChanged: () => void;
  /** Opens the drawer's existing tier-3 `clear_pairing` flow for the `⋯` menu's "Re-provision". */
  onReprovision: () => void;
}) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  /**
   * "No hardware unit is joined to this check-in" is a DEFINITE claim, and it
   * is only ours to make when the lookup that would have found the unit
   * actually answered. When this device is `registerUnresolved` we do not know
   * whether a unit is paired — the pill and the sentence below are suppressed
   * and the "register unavailable — lookup failed" pill stands alone. Saying
   * both at once was the card stating an answer it never got.
   */
  const noUnit = row.hasDeviceRecord && !row.hasUnitRecord && !registerUnresolved;
  /**
   * The unit on this row was GUESSED — matched by finding the tracking id
   * inside the register name — rather than STATED by the registers table.
   * Said quietly, because a guessed join is still the only join a register
   * created before the link existed will ever have.
   */
  const joinGuessed = row.unitJoinSource === 'name';
  const neverCheckedIn = !row.hasDeviceRecord && row.hasUnitRecord;
  const modelName = model?.modelName;
  // VISIBLE text only ever names the device — never an id. `deviceId`,
  // `serialNumber` and `unitId` are internal/physical identifiers, not names;
  // they stay out of this fallback chain and live in the `title` below.
  const resolvedName = row.registerName || modelName;
  const nameUnknown = !resolvedName;
  const displayName = resolvedName || 'Register name unavailable';

  const hwMatch = classifyHardwareMatch(row.hardwareModel, model?.appProfile);
  const mismatched = isHardwareMismatch(hwMatch);

  const blocked = reinstallBlock(row);
  const supportBlocked = !row.deviceId ? 'No device has checked in under this unit, so there is nothing to talk to.' : undefined;
  const historyBlocked = !row.deviceId ? 'No check-in history exists for this unit.' : undefined;

  const seenStale = !!row.updatedAt && Date.now() - row.updatedAt > STALE_SEEN_MS;

  const copyCode = async () => {
    if (!row.provisionCode) return;
    try {
      await navigator.clipboard.writeText(row.provisionCode);
      setCopied(true);
      toastService.success('Provisioning code copied');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toastService.error('Could not copy — copy it by hand');
    }
  };

  return (
    <article
      className={`flex flex-col gap-2.5 rounded-2xl border bg-white p-3.5 shadow-sm ${
        mismatched ? 'border-rose-200 ring-2 ring-rose-100' : 'border-gray-200'
      }`}
    >
      {/* ── head ── */}
      <div className="flex items-start gap-2.5">
        <ShimmerImage
          src={model?.media?.heroImageUrl}
          alt={modelName || resolvedName || 'Device'}
          width={46}
          height={46}
          objectFit="contain"
          className="shrink-0 rounded-xl border border-gray-200"
          fallbackBgColor="bg-gray-100"
          fallbackIcon={<Monitor size={18} className="text-gray-400" />}
        />
        <div className="min-w-0 flex-1">
          <div
            className={`break-words text-[15px] font-extrabold leading-tight ${nameUnknown ? 'italic text-gray-500' : 'text-gray-900'}`}
            title={`deviceId ${row.deviceId || '—'}${row.unitId ? ` · unitId ${row.unitId}` : ''}${
              // Two different facts with two different remedies: no device record
              // means the unit has never checked in (the register lookup may have
              // succeeded fine); registerUnresolved means the lookup itself failed.
              registerUnresolved ? ' — register lookup failed' : (!row.hasDeviceRecord ? ' — never checked in' : '')
            }`}
          >
            {displayName}
          </div>
          {(tenantName || row.locationName || registerUnresolved) && (
            <div className="mt-0.5 break-words text-[12.5px] text-gray-500" title={`tenantId ${row.tenantId || '—'}${row.locationId ? ` · locationId ${row.locationId}` : ''}`}>
              {tenantName || (registerUnresolved ? 'business unavailable' : null)}
              {(tenantName || registerUnresolved) && (row.locationName || registerUnresolved) && ' · '}
              <span className="text-gray-400">{row.locationName || (registerUnresolved ? 'location unavailable' : '')}</span>
            </div>
          )}
        </div>
      </div>

      {mismatched && (
        <div className="flex items-start gap-1.5 rounded-lg border border-red-300 bg-red-50 px-2 py-1.5 text-[11.5px] font-semibold text-red-700">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>
            Reports <span className="font-mono">{row.hardwareModel}</span> — registered as {modelName || row.modelId}
          </span>
        </div>
      )}

      {/* ── pills ── */}
      <div className="flex flex-wrap items-center gap-1.5">
        {registerUnresolved && (
          <span
            className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-500"
            title="The register lookup failed for this device — this is not the same as having no register. Whether a hardware unit is paired is unknown too."
          >
            register unavailable — lookup failed
          </span>
        )}
        {joinGuessed && (
          <span
            className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-bold text-gray-500"
            title="No register row states this pairing. The unit was matched by finding its tracking id inside the register name — likely right, but not stated."
          >
            unit matched by name
          </span>
        )}
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-extrabold ${STATUS_STYLE[row.releaseStatus].cls}`}>
          {STATUS_STYLE[row.releaseStatus].label}
        </span>
        {/*
          Task 10 — the remote off switch. On the FACE of the card, never
          only in the menu: a till that will not update itself has to say so
          where you can see it without clicking. Absent from the pill row
          entirely when autoUpdate is true — nothing to warn about.
        */}
        {!row.autoUpdate && (
          <span
            className="rounded-full bg-rose-500/15 px-2 py-0.5 text-[11px] font-extrabold text-rose-600"
            title="This till will not install app updates until auto-update is turned back on"
          >
            auto-update OFF
          </span>
        )}
        {noUnit && (
          <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-extrabold text-amber-600" title="No hardware unit is joined to this check-in">
            no unit joined
          </span>
        )}
        {neverCheckedIn && (
          <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-[11px] font-extrabold text-sky-600">
            installed · never checked in
          </span>
        )}
        {order && row.orderId && (
          <button
            type="button"
            onClick={() => navigate(`/inventory/orders?open=${encodeURIComponent(row.orderId!)}`)}
            className="rounded-full bg-sky-500/15 px-2 py-0.5 text-[11px] font-extrabold text-sky-700 hover:underline"
            title={`orderId ${row.orderId}`}
          >
            {order.orderNo} · {order.status} ↗
          </button>
        )}
      </div>

      {/* SUNMI T3 only — primary Tap to Pay + backup reader status from the latest heartbeat. */}
      {row.deviceId && (isCardSetupModel(model) || isSunmiT3Model(model)) && <T3CardReadersLine deviceId={row.deviceId} />}

      {/* ── identity ── */}
      {(row.trackingId || row.serialNumber || row.provisionCode) ? (
        <div className="flex flex-col gap-1 rounded-xl bg-gray-50 px-2.5 py-2 text-[11.5px]">
          {row.trackingId && (
            <div className="flex items-center gap-2">
              <span className="w-[58px] shrink-0 text-[10px] font-bold uppercase tracking-wide text-gray-400">Tracking</span>
              <span className="font-mono text-gray-900">{row.trackingId}</span>
            </div>
          )}
          {row.serialNumber && (
            <div className="flex items-center gap-2">
              <span className="w-[58px] shrink-0 text-[10px] font-bold uppercase tracking-wide text-gray-400">Serial</span>
              <span className="font-mono text-gray-900">{row.serialNumber}</span>
            </div>
          )}
          {row.provisionCode && (
            <div className="flex items-center gap-2">
              <span className="w-[58px] shrink-0 text-[10px] font-bold uppercase tracking-wide text-gray-400">Code</span>
              <span className="font-mono font-bold tracking-wide text-[#9F1239]">{row.provisionCode}</span>
              <button
                type="button"
                onClick={copyCode}
                title="Copy provisioning code"
                className="ml-auto inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2 text-[10.5px] font-bold text-[#9F1239] hover:bg-rose-100"
              >
                {copied ? '✓ Copied' : '⧉ Copy'}
              </button>
            </div>
          )}
        </div>
      ) : noUnit ? (
        <div className="rounded-xl bg-gray-50 px-2.5 py-2 text-[11.5px] italic text-gray-400">
          No hardware unit is paired to this check-in — it has no tracking id, serial, provisioning code or
          order. It registered itself by checking in.
        </div>
      ) : null}

      {/* ── release strip ── */}
      <div className="flex flex-wrap items-center gap-1.5 text-[11.5px]">
        {row.displayVersion ? (
          <span className="font-mono font-bold text-gray-900" title={row.displayVersion}>
            {shortVersion(row.displayVersion)}
          </span>
        ) : (
          <span className="text-gray-400">no version reported</span>
        )}
        <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-extrabold ${RING_STYLE[row.releaseRing] || RING_STYLE.stable}`}>
          {row.releaseRing}
        </span>
        {row.pinnedVersion && (
          <span className="rounded-full bg-[#FFF1F3] px-2 py-0.5 text-[10.5px] font-extrabold text-[#9F1239]">
            pinned {row.pinnedVersion}
          </span>
        )}
        <span className="text-gray-400">
          target{' '}
          <b className="font-mono font-semibold text-gray-600">
            {row.targetVersion || 'unknown'}
          </b>
          {row.targetSource && (
            <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[9.5px] font-extrabold ${TARGET_SOURCE_STYLE[row.targetSource].cls}`}>
              {TARGET_SOURCE_STYLE[row.targetSource].label}
            </span>
          )}
        </span>
        {row.installSource && (
          <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-extrabold ${SOURCE_STYLE[row.installSource] || SOURCE_STYLE.local}`}>
            {row.installSource}
          </span>
        )}
      </div>

      {/* ── branding trio ── */}
      <div className="grid grid-cols-3 gap-1.5">
        <div className="rounded-lg border border-gray-200 px-1.5 py-1">
          <div className="text-[9.5px] font-bold uppercase tracking-wide text-gray-400">Boot</div>
          <div className="text-[12px] font-bold text-gray-700">{row.bootVersion == null ? '—' : `v${row.bootVersion}`}</div>
        </div>
        <div className="rounded-lg border border-gray-200 px-1.5 py-1">
          <div className="text-[9.5px] font-bold uppercase tracking-wide text-gray-400">App load</div>
          <div className="text-[12px] font-bold text-gray-700">{row.loadingVersion == null ? '—' : `v${row.loadingVersion}`}</div>
        </div>
        <div className="rounded-lg border border-gray-200 px-1.5 py-1">
          <div className="text-[9.5px] font-bold uppercase tracking-wide text-gray-400">Install</div>
          <div className="text-[12px]">{installCell(row)}</div>
        </div>
      </div>

      {/* ── last seen ── */}
      <div className="flex flex-wrap items-center gap-1.5 text-[11.5px] text-gray-500">
        {row.updatedAt ? (
          <span>
            Last check-in{' '}
            <b className={seenStale ? 'font-extrabold text-amber-600' : 'font-extrabold text-gray-900'}>
              {checkInAgo(row.updatedAt)}
            </b>
          </span>
        ) : neverCheckedIn ? (
          <span>
            <span className="font-semibold italic text-gray-400">Never checked in</span> — known from the install
            record only
          </span>
        ) : (
          <span className="text-gray-400">No check-in recorded</span>
        )}
        {row.installedAt !== undefined && (
          <span className="text-gray-400" title={installedAtFull(row.installedAt, row.installedBy)}>
            · installed {installedOn(row.installedAt)}
          </span>
        )}
      </div>

      {/* ── actions ── */}
      <div className="mt-auto flex flex-wrap items-center gap-1 border-t border-gray-100 pt-2">
        <button
          type="button"
          onClick={onSupport}
          disabled={!!supportBlocked}
          title={supportBlocked || 'Open the support console for this device'}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl px-2.5 text-[11.5px] font-extrabold text-gray-600 hover:bg-[#FFF1F3] hover:text-[#9F1239] disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
        >
          <LifeBuoy size={14} /> Support
        </button>
        <button
          type="button"
          onClick={onHistory}
          disabled={!!historyBlocked}
          title={historyBlocked || 'Version and command history for this device'}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl px-2.5 text-[11.5px] font-extrabold text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
        >
          <History size={14} /> History
        </button>
        <button
          type="button"
          onClick={onReinstall}
          disabled={!!blocked}
          title={blocked || 'Send this till back to Pending Configuration to be installed again'}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl px-2.5 text-[11.5px] font-extrabold text-gray-600 hover:bg-amber-50 hover:text-amber-700 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
        >
          <RotateCcw size={14} /> Reinstall
        </button>

        {row.deviceId && (
          <button
            type="button"
            onClick={onFlipChannel}
            title="Flip this device's branding channel"
            className={`inline-flex min-h-[44px] items-center gap-1 rounded-xl px-2 text-[10.5px] font-extrabold ${row.assignedChannel === 'canary' ? 'bg-amber-500/15 text-amber-600' : 'bg-blue-500/15 text-blue-600'}`}
          >
            {row.assignedChannel || 'stable'} ⇄
          </button>
        )}

        {row.deviceId && (
          <button
            type="button"
            onClick={onRemove}
            title="Remove this device from the fleet"
            aria-label={`Remove ${row.registerName || row.deviceId} from the fleet`}
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl text-gray-400 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 size={14} />
          </button>
        )}

        <div className="ml-auto">
          <FleetRowMenu row={row} onChanged={onChanged} onReprovision={onReprovision} />
        </div>
      </div>
    </article>
  );
}
